import { useEffect, useRef, useState } from 'react'
import {
  ChevronRight,
  Crosshair,
  Loader2,
  MapPin,
  Navigation,
  Search,
  X,
} from 'lucide-react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

/**
 * Professional Hybrid Reverse Geocoder:
 * 1. TomTom Nearby Search (radius 150m) untuk menangkap POI/Toko/Bangunan terdekat & jalan.
 * 2. TomTom Reverse Geocode (radius 100m) untuk presisi nomor rumah & jalan.
 * 3. Administrative Boundary Fusion: Jika desa/kelurahan atau kecamatan kosong di TomTom,
 *    otomatis diperkaya dari layer wilayah OSM sehingga alamat SELALU lengkap sampai tingkat Desa & Kecamatan.
 */
export async function reverseGeocodeFull(lat, lng) {
  const tomtomKey = import.meta.env.VITE_TOMTOM_API_KEY?.trim()

  let resolvedPoi = null
  let resolvedStreet = null
  let resolvedVillage = null
  let resolvedSubdistrict = null
  let resolvedRegency = null
  let resolvedProvince = null
  let resolvedCountry = 'Indonesia'
  let resolvedPostcode = null
  let fallbackText = null

  // 1. Coba TomTom (Nearby Search + Reverse Geocode)
  if (tomtomKey) {
    try {
      // Step A: Nearby Search (radius 150m) - sangat akurat untuk menemukan toko/POI & desa sekitar titik klik
      const nearbyUrl =
        `https://api.tomtom.com/search/2/nearbySearch/.json` +
        `?key=${tomtomKey}&lat=${lat}&lon=${lng}&radius=150&limit=1&countrySet=ID&language=id-ID`

      const nearbyRes = await fetch(nearbyUrl)
      if (nearbyRes.ok) {
        const nearbyData = await nearbyRes.json()
        const hit = nearbyData.results?.[0]
        if (hit?.address) {
          const a = hit.address
          resolvedPoi = hit.poi?.name || null
          resolvedStreet = a.streetNumber ? `${a.streetName} No. ${a.streetNumber}` : a.streetName || null
          resolvedVillage = a.municipalitySubdivision || a.neighbourhood || null
          resolvedSubdistrict = a.municipality || null
          resolvedRegency = a.countrySecondarySubdivision || null
          resolvedProvince = a.countrySubdivision || null
          resolvedPostcode = a.postalCode || null
          fallbackText = a.freeformAddress || null
        }
      }

      // Step B: Jika jalan masih kosong, coba Reverse Geocode TomTom
      if (!resolvedStreet) {
        const revUrl = `https://api.tomtom.com/search/2/reverseGeocode/${lat},${lng}.json?key=${tomtomKey}&language=id-ID&radius=100`
        const revRes = await fetch(revUrl)
        if (revRes.ok) {
          const revData = await revRes.json()
          const item = revData.addresses?.[0]
          if (item?.address) {
            const a = item.address
            resolvedStreet = a.streetNumber ? `${a.streetName} No. ${a.streetNumber}` : a.streetName || resolvedStreet
            resolvedVillage = resolvedVillage || a.municipalitySubdivision || a.neighbourhood || null
            resolvedSubdistrict = resolvedSubdistrict || a.municipality || null
            resolvedRegency = resolvedRegency || a.countrySecondarySubdivision || null
            resolvedProvince = resolvedProvince || a.countrySubdivision || null
            resolvedPostcode = resolvedPostcode || a.postalCode || null
            fallbackText = fallbackText || a.freeformAddress || null
          }
        }
      }
    } catch (err) {
      console.warn('[TomTom Geocoder] error:', err)
    }
  }

  // 2. Administrative Enrichment: Jika Desa atau Kecamatan belum lengkap, ambil dari boundary OSM
  if (!resolvedVillage || !resolvedSubdistrict || !resolvedPoi) {
    try {
      const osmUrl =
        `https://nominatim.openstreetmap.org/reverse` +
        `?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&namedetails=1&extratags=1`

      const osmRes = await fetch(osmUrl, {
        headers: { 'Accept-Language': 'id-ID,id;q=0.9,en;q=0.8' },
      })

      if (osmRes.ok) {
        const osmData = await osmRes.json()
        const addr = osmData.address || {}
        const namedetails = osmData.namedetails || {}

        const osmPoi =
          namedetails['name:id'] ||
          namedetails.name ||
          osmData.name ||
          addr.amenity ||
          addr.shop ||
          addr.building ||
          addr.office ||
          null

        const osmRoad = addr.road || addr.street || addr.pedestrian || addr.highway || null
        const osmVillage = addr.village || addr.suburb || addr.hamlet || addr.neighbourhood || addr.quarter || null
        const osmSubdistrict = addr.subdistrict || addr.city_district || addr.town || null
        const osmRegency = addr.county || addr.city || addr.municipality || addr.state_district || null
        const osmProvince = addr.state || addr.region || addr.province || null
        const osmPostcode = addr.postcode || null

        resolvedPoi = resolvedPoi || osmPoi
        resolvedStreet = resolvedStreet || osmRoad
        resolvedVillage = resolvedVillage || osmVillage
        resolvedSubdistrict = resolvedSubdistrict || osmSubdistrict
        resolvedRegency = resolvedRegency || osmRegency
        resolvedProvince = resolvedProvince || osmProvince
        resolvedPostcode = resolvedPostcode || osmPostcode
        fallbackText = fallbackText || osmData.display_name || null
      }
    } catch (err) {
      console.warn('[OSM Enrichment] error:', err)
    }
  }

  // 3. Susun Hirarki Alamat Lengkap & Bersih
  const rawParts = [
    resolvedPoi,
    resolvedStreet,
    resolvedVillage,
    resolvedSubdistrict,
    resolvedRegency,
    resolvedProvince,
    resolvedCountry,
    resolvedPostcode,
  ].filter(Boolean)

  const seen = new Set()
  const uniqueParts = rawParts.filter((p) => {
    if (seen.has(p)) return false
    seen.add(p)
    return true
  })

  const title = resolvedPoi || resolvedStreet || resolvedVillage || resolvedSubdistrict || 'Lokasi Terpilih'
  const fullAddress =
    uniqueParts.length >= 2
      ? uniqueParts.join(', ')
      : fallbackText || `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`

  return {
    title,
    fullAddress,
    lat: Number(lat),
    lng: Number(lng),
  }
}

/**
 * Pencarian alamat & POI dengan TomTom Fuzzy Search + bias koordinat
 * dengan fallback ke OpenStreetMap.
 */
async function searchAddresses(keyword, centerCoords = null) {
  const tomtomKey = import.meta.env.VITE_TOMTOM_API_KEY?.trim()
  const results = []

  // 1. Coba TomTom Fuzzy Search API
  if (tomtomKey) {
    try {
      let url =
        `https://api.tomtom.com/search/2/search/${encodeURIComponent(keyword)}.json` +
        `?key=${tomtomKey}&countrySet=ID&language=id-ID&typeahead=true&limit=8`

      if (centerCoords?.lat && centerCoords?.lng) {
        url += `&lat=${centerCoords.lat}&lon=${centerCoords.lng}&radius=50000`
      }

      const res = await fetch(url)
      if (res.ok) {
        const data = await res.json()
        for (const item of data.results || []) {
          const addr = item.address || {}
          const poiName = item.poi?.name || null
          const street = addr.streetNumber
            ? `${addr.streetName} No. ${addr.streetNumber}`
            : addr.streetName || null
          const village = addr.municipalitySubdivision || addr.neighbourhood || null
          const subdistrict = addr.municipality || null
          const regency = addr.countrySecondarySubdivision || null
          const province = addr.countrySubdivision || null
          const postcode = addr.postalCode || null
          const country = addr.country || 'Indonesia'

          const rawParts = [
            poiName,
            street,
            village,
            subdistrict,
            regency,
            province,
            country,
            postcode,
          ].filter(Boolean)

          const seen = new Set()
          const uniqueParts = rawParts.filter((p) => {
            if (seen.has(p)) return false
            seen.add(p)
            return true
          })

          const title = poiName || street || village || subdistrict || addr.freeformAddress || 'Lokasi'
          const fullAddress = uniqueParts.length >= 2 ? uniqueParts.join(', ') : addr.freeformAddress || ''

          results.push({
            id: `tomtom-${item.id || Math.random()}`,
            title,
            fullAddress,
            subtitle: fullAddress,
            lat: item.position?.lat,
            lng: item.position?.lon,
            source: 'tomtom',
          })
        }
      }
    } catch (err) {
      console.warn('[TomTom Search] error, falling back to OSM:', err)
    }
  }

  if (results.length > 0) {
    return results
  }

  // 2. Fallback ke Nominatim OSM
  try {
    const nomUrl =
      `https://nominatim.openstreetmap.org/search` +
      `?format=jsonv2&q=${encodeURIComponent(keyword)}` +
      `&countrycodes=id&limit=8&addressdetails=1&namedetails=1&extratags=1`

    const nomRes = await fetch(nomUrl, {
      headers: { 'Accept-Language': 'id-ID,id;q=0.9,en;q=0.8' },
    })

    if (nomRes.ok) {
      const list = await nomRes.json()
      for (const item of list) {
        const addr = item.address || {}
        const namedetails = item.namedetails || {}

        const poiName =
          namedetails['name:id'] ||
          namedetails.name ||
          item.name ||
          addr.amenity ||
          addr.shop ||
          addr.building ||
          addr.office ||
          addr.tourism ||
          null

        const road = addr.road || addr.street || addr.pedestrian || null
        const village = addr.village || addr.hamlet || addr.suburb || addr.neighbourhood || null
        const subdistrict = addr.subdistrict || addr.city_district || addr.town || null
        const regency = addr.county || addr.city || addr.municipality || null
        const province = addr.state || addr.region || null
        const country = addr.country || 'Indonesia'
        const postcode = addr.postcode || null

        const rawParts = [poiName, road, village, subdistrict, regency, province, country, postcode].filter(Boolean)
        const seen = new Set()
        const uniqueParts = rawParts.filter((p) => { if (seen.has(p)) return false; seen.add(p); return true })

        const title = poiName || road || village || subdistrict || item.display_name?.split(',')[0] || 'Lokasi'
        const fullAddress = uniqueParts.length >= 2 ? uniqueParts.join(', ') : item.display_name || ''

        results.push({
          id: `nom-${item.place_id}`,
          title,
          fullAddress,
          subtitle: fullAddress,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          source: 'nominatim',
        })
      }
    }
  } catch (err) {
    console.warn('[Nominatim Search] error:', err)
  }

  return results
}

export function AddressModal({ isOpen, onClose, initialCoords, initialAddress, onSelectLocation }) {
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [locating, setLocating] = useState(false)
  const [activeTab, setActiveTab] = useState('search')
  const [selectedPreview, setSelectedPreview] = useState(null)
  const [noResults, setNoResults] = useState(false)

  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markerInstanceRef = useRef(null)
  const inputRef = useRef(null)

  const defaultLat = initialCoords?.lat || -6.2088
  const defaultLng = initialCoords?.lng || 106.8456

  // Debounced auto-search
  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 3) {
      setSearchResults([])
      setNoResults(false)
      setSearching(false)
      return
    }

    setSearching(true)
    setNoResults(false)

    const timer = setTimeout(async () => {
      try {
        const res = await searchAddresses(trimmed, selectedPreview || initialCoords)
        setSearchResults(res)
        setNoResults(res.length === 0)
      } catch {
        setSearchResults([])
        setNoResults(true)
      } finally {
        setSearching(false)
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [query])

  // Init selected preview from initial address
  useEffect(() => {
    if (isOpen && initialCoords?.lat && initialCoords?.lng) {
      setSelectedPreview({
        title: 'Lokasi Anda',
        fullAddress: initialAddress || `${initialCoords.lat.toFixed(6)}, ${initialCoords.lng.toFixed(6)}`,
        lat: initialCoords.lat,
        lng: initialCoords.lng,
      })
    }
    if (isOpen) {
      setQuery('')
      setSearchResults([])
      setNoResults(false)
    }
  }, [isOpen])

  // Init Leaflet map with Google Maps tiles
  useEffect(() => {
    if (!isOpen || activeTab !== 'map' || !mapContainerRef.current) return

    const centerLat = selectedPreview?.lat || defaultLat
    const centerLng = selectedPreview?.lng || defaultLng

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
    }

    const map = L.map(mapContainerRef.current, {
      zoomControl: false,
      attributionControl: false,
    }).setView([centerLat, centerLng], 17)

    // Google Maps tile
    L.tileLayer('https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
      maxZoom: 20,
    }).addTo(map)

    L.control.zoom({ position: 'bottomright' }).addTo(map)

    const customIcon = L.divIcon({
      className: 'ecatalog-custom-pin',
      html: `<div class="ecatalog-map-pin-badge">
        <svg width="30" height="38" viewBox="0 0 24 24" fill="#e53935" stroke="#ffffff" stroke-width="1.5">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
          <circle cx="12" cy="9" r="2.5" fill="#ffffff"/>
        </svg>
      </div>`,
      iconSize: [30, 38],
      iconAnchor: [15, 38],
    })

    const marker = L.marker([centerLat, centerLng], {
      icon: customIcon,
      draggable: true,
    }).addTo(map)

    marker.on('dragend', async () => {
      const pos = marker.getLatLng()
      map.panTo(pos)
      const details = await reverseGeocodeFull(pos.lat, pos.lng)
      setSelectedPreview(details)
    })

    map.on('click', async (e) => {
      marker.setLatLng(e.latlng)
      map.panTo(e.latlng)
      const details = await reverseGeocodeFull(e.latlng.lat, e.latlng.lng)
      setSelectedPreview(details)
    })

    mapInstanceRef.current = map
    markerInstanceRef.current = marker
    setTimeout(() => map.invalidateSize(), 250)

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [isOpen, activeTab])

  if (!isOpen) return null

  const handleSelectResult = (result) => {
    onSelectLocation({
      title: result.title,
      fullAddress: result.fullAddress,
      lat: result.lat,
      lng: result.lng,
    })
    onClose()
  }

  const handleUseDeviceLocation = () => {
    if (!navigator.geolocation) {
      alert('Perangkat tidak mendukung GPS lokasi.')
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const details = await reverseGeocodeFull(coords.latitude, coords.longitude)
        setLocating(false)
        onSelectLocation(details)
        onClose()
      },
      (err) => {
        setLocating(false)
        console.warn('Geolocation error:', err)
        alert('Izin lokasi belum diberikan. Aktifkan izin lokasi di pengaturan browser Anda.')
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    )
  }

  const handleConfirmMapLocation = () => {
    if (!selectedPreview) return
    onSelectLocation(selectedPreview)
    onClose()
  }

  return (
    <div
      className="ecatalog-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="ecatalog-address-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="address-modal-title"
      >
        {/* Header */}
        <div className="ecatalog-address-modal-header">
          <div>
            <h2 id="address-modal-title">Pilih Alamat</h2>
            <p className="ecatalog-address-modal-sub">
              Tentukan lokasi pengiriman untuk melihat outlet terdekat
            </p>
          </div>
          <button type="button" className="ecatalog-icon-button" onClick={onClose} aria-label="Tutup">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="ecatalog-address-tabs">
          <button
            type="button"
            className={`ecatalog-address-tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            <Search size={15} /> Cari Alamat
          </button>
          <button
            type="button"
            className={`ecatalog-address-tab ${activeTab === 'map' ? 'active' : ''}`}
            onClick={() => setActiveTab('map')}
          >
            <MapPin size={15} /> Pilih Lewat Peta
          </button>
        </div>

        {/* Tab: Search */}
        {activeTab === 'search' && (
          <div className="ecatalog-address-search-view">
            {/* GPS Button */}
            <button
              type="button"
              className="ecatalog-address-current-gps-btn"
              onClick={handleUseDeviceLocation}
              disabled={locating}
            >
              <span className="ecatalog-address-current-gps-icon">
                {locating
                  ? <Loader2 size={17} className="ecatalog-spin" />
                  : <Navigation size={17} />}
              </span>
              <span className="ecatalog-address-current-gps-text">
                <strong>Gunakan lokasi kamu saat ini</strong>
                <small>
                  {locating
                    ? 'Mendeteksi koordinat GPS...'
                    : 'Deteksi otomatis lewat GPS perangkat'}
                </small>
              </span>
            </button>

            {/* Search Input */}
            <div className="ecatalog-address-search-form">
              {searching
                ? <Loader2 size={18} className="ecatalog-address-search-icon ecatalog-spin" />
                : <Search size={18} className="ecatalog-address-search-icon" />}
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ketik nama toko, tempat, jalan, atau desa..."
                aria-label="Cari alamat"
                autoFocus
              />
              {query && (
                <button
                  type="button"
                  className="ecatalog-search-clear-btn"
                  onClick={() => {
                    setQuery('')
                    inputRef.current?.focus()
                  }}
                  aria-label="Hapus"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* Hint */}
            {!query && (
              <p className="ecatalog-search-hint">
                Ketik nama tempat usaha, toko, jalan, atau desa untuk mencari lokasi secara otomatis.
                Contoh: <em>Percetakan</em>, <em>Jl. Raya Sidomakmur</em>, <em>Tegal Rejo</em>
              </p>
            )}

            {/* Results */}
            {searching && query.trim().length >= 3 && (
              <div className="ecatalog-address-loading">
                <Loader2 size={18} className="ecatalog-spin" style={{ margin: '0 auto 8px', display: 'block' }} />
                <p>Mencari <strong>{query}</strong>...</p>
              </div>
            )}

            {!searching && searchResults.length > 0 && (
              <div className="ecatalog-address-results-list">
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    className="ecatalog-address-result-item"
                    onClick={() => handleSelectResult(result)}
                  >
                    <MapPin size={17} className="ecatalog-pin-red" style={{ flexShrink: 0 }} />
                    <div className="ecatalog-address-result-info">
                      <strong>{result.title}</strong>
                      <p>{result.fullAddress}</p>
                    </div>
                    <ChevronRight size={15} className="ecatalog-address-result-arrow" />
                  </button>
                ))}
              </div>
            )}

            {!searching && noResults && query.trim().length >= 3 && (
              <div className="ecatalog-address-empty">
                <MapPin size={22} style={{ margin: '0 auto 8px', display: 'block', color: '#d1d5db' }} />
                <p><strong>{query}</strong> tidak ditemukan.</p>
                <p style={{ marginTop: 6, fontSize: '0.76rem' }}>
                  Coba ketik nama jalan, desa, atau pilih titik langsung melalui tab <strong>Pilih Lewat Peta</strong>.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tab: Map */}
        {activeTab === 'map' && (
          <div className="ecatalog-address-map-view">
            <div className="ecatalog-leaflet-wrapper">
              <div ref={mapContainerRef} className="ecatalog-leaflet-container" />
              <button
                type="button"
                className="ecatalog-map-gps-overlay-btn"
                onClick={handleUseDeviceLocation}
                disabled={locating}
                title="Pusatkan ke lokasi saya"
              >
                {locating
                  ? <Loader2 size={17} className="ecatalog-spin" />
                  : <Crosshair size={18} />}
              </button>
            </div>

            {selectedPreview ? (
              <div className="ecatalog-map-preview-card">
                <div className="ecatalog-map-preview-icon">
                  <MapPin size={20} className="ecatalog-pin-red" />
                </div>
                <div className="ecatalog-map-preview-body">
                  <span className="ecatalog-map-preview-tag">Titik Terpilih</span>
                  <strong className="ecatalog-map-preview-title">{selectedPreview.title}</strong>
                  <p className="ecatalog-map-preview-desc">{selectedPreview.fullAddress}</p>
                </div>
                <button
                  type="button"
                  className="ecatalog-btn-confirm-location"
                  onClick={handleConfirmMapLocation}
                >
                  Pilih
                </button>
              </div>
            ) : (
              <div className="ecatalog-map-preview-card ecatalog-map-preview-placeholder">
                <p>Geser pin atau klik di peta untuk memilih lokasi.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
